import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { Controller, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import {
  selectProject,
  selectResearchFilters,
  setProject,
  setResearchFilter,
} from '../../model/researchSlice'
import { IResearchFilter } from '../../model/types/ResearchFilter.interface'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { Trans, useTranslation } from 'react-i18next'
import React from 'react'
import { NavLink } from 'react-router-dom'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import clsx from 'clsx'

import styles from './AdvertisingResearchFilters.module.scss'
import {
  GetFilterAdvertiserDomainsApiArg,
  useGetFilterAdvertiserDomainsQuery,
  useGetFilterCountryQuery,
  useGetFilterProjectsQuery,
} from '../../../../app/api'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import { DomainIcon } from '../../../../shared/components/icons/filters/DomainIcon'
import { toTextOptions } from '../../../../shared/lib/helpers/ToTextOptions'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'

const Filter = ({ searchParams, setSearchParams }: IWithSearchParamsProps<IResearchFilter>) => {
  const { t } = useTranslation()
  const { domain, country, project } = useAppSelector<IResearchFilter>(selectResearchFilters)
  const { is_demo: isDemo } = useAppSelector(selectProject)
  const dispatch = useAppDispatch()
  const [search, setSearch] = useSearchState({ domain: '' })

  const {
    getValues,
    setValue,
    control,
    formState: { defaultValues },
  } = useForm<IResearchFilter>({
    mode: 'onChange',
    defaultValues: {
      domain,
      country,
      project,
      ...searchParams,
    },
  })

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery(
    {
      projectId: project ? +project : undefined,
    },
    {
      skip: !project,
    },
  )

  const {
    data: domains,
    isFetching: domainsFetching,
    isLoading: domainsLoading,
  } = useGetFilterAdvertiserDomainsQuery(
    {
      projectId: project,
      search: search.domain,
      seo: false,
    } as unknown as GetFilterAdvertiserDomainsApiArg,
    { skip: !project },
  )

  const applyFilter = () => {
    const values = getValues()
    dispatch(setResearchFilter(getValues()))
    if (setSearchParams) setSearchParams(values)
  }

  return (
    <section>
      <form className={clsx(styles.form)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue =
                defaultValues?.project ??
                project ??
                projects?.find((option) => !option.is_demo)?.value ??
                projects?.[0]?.value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={ProjectIcon}
                  bottomSection={<AddProjectButton />}
                  defaultValue={defaultValue}
                  disabled={projectsLoading || projectsFetching}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  title={t('fields.project')}
                  onChange={(e) => {
                    const option = projects?.find((option) => option.value === e.target.value)

                    if (option) {
                      dispatch(setProject(option))
                    }

                    onChange(e)
                    if (e.target.value !== defaultValue) {
                      setValue('country', [])
                      setValue('domain', '')
                    }
                    applyFilter()
                  }}
                />
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={clsx(styles.fieldWide)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={CountryIcon}
                bottomSection={<EditCountriesButton project={{ value: project, isDemo }} />}
                defaultValue={defaultValues?.country as string[]}
                disabled={countriesLoading || countriesFetching}
                fieldSize='lg'
                loading={countriesLoading || countriesFetching}
                optionRender={renderCountryOption}
                options={countries}
                title={t('fields.country')}
                onChange={(e) => {
                  onChange([].slice.call(e.target.selectedOptions).map((o: IOption) => o.value))
                }}
                onDropClose={applyFilter}
              />
            )}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => {
              return (
                <BpSelect
                  {...fieldProps}
                  multiple
                  Icon={DomainIcon}
                  enableChipLoad={false}
                  fieldSize='lg'
                  loading={domainsLoading || domainsFetching}
                  options={toTextOptions(domains, value)}
                  placeholder={t('fields.domain')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onDropClose={applyFilter}
                  onSearch={setSearch('domain')}
                />
              )
            }}
            control={control}
            name='domain'
          />
        </div>
      </form>
      {isDemo && (
        <div className={clsx(styles.demoBanner)}>
          <svg
            className={clsx(styles.demoIcon)}
            fill='none'
            height='20'
            viewBox='0 0 20 20'
            width='20'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              clipRule='evenodd'
              d='M5.67678 1.76256C6.00497 1.43437 6.45009 1.25 6.91421 1.25H13.0858C13.5499 1.25 13.995 1.43437 14.3232 1.76256L18.2374 5.67678C18.5656 6.00497 18.75 6.45009 18.75 6.91421V13.0858C18.75 13.5499 18.5656 13.995 18.2374 14.3232L14.3232 18.2374C13.995 18.5656 13.5499 18.75 13.0858 18.75H6.91421C6.45009 18.75 6.00497 18.5656 5.67678 18.2374L1.76256 14.3232C1.43437 13.995 1.25 13.5499 1.25 13.0858V6.91421C1.25 6.45009 1.43437 6.00497 1.76256 5.67678L5.67678 1.76256ZM6.91421 2.75C6.84791 2.75 6.78432 2.77634 6.73744 2.82322L2.82322 6.73744C2.77634 6.78432 2.75 6.84791 2.75 6.91421V13.0858C2.75 13.1521 2.77634 13.2157 2.82322 13.2626L6.73744 17.1768C6.78432 17.2237 6.84791 17.25 6.91421 17.25H13.0858C13.1521 17.25 13.2157 17.2237 13.2626 17.1768L17.1768 13.2626C17.2237 13.2157 17.25 13.1521 17.25 13.0858V6.91421C17.25 6.84791 17.2237 6.78432 17.1768 6.73744L13.2626 2.82322C13.2157 2.77634 13.1521 2.75 13.0858 2.75H6.91421ZM10 8.25C10.4142 8.25 10.75 8.58579 10.75 9V13C10.75 13.4142 10.4142 13.75 10 13.75C9.58579 13.75 9.25 13.4142 9.25 13V9C9.25 8.58579 9.58579 8.25 10 8.25ZM10 7.5C10.4142 7.5 10.75 7.16421 10.75 6.75C10.75 6.33579 10.4142 6 10 6C9.58579 6 9.25 6.33579 9.25 6.75C9.25 7.16421 9.58579 7.5 10 7.5Z'
              fill='#00326D'
              fillRule='evenodd'
            />
          </svg>
          <p className={clsx(styles.demoText)}>
            <Trans i18nKey='other.demoProjectSelected'>
              <NavLink className={clsx(styles.demoLink)} to='/projects' />
            </Trans>
          </p>
        </div>
      )}
    </section>
  )
}

export const AdvertisingResearchFilters = withSearchParams<
  IWithSearchParamsProps<IResearchFilter>,
  IResearchFilter
>(Filter, selectResearchFilters)
